import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  AllocationSummary,
  CaseDetail as CaseDetailType,
  CaseStatus,
  DonationAllocationState,
} from '@foundation/shared';
import {
  CASE_PRIORITY_LABEL_TH,
  CASE_STATUS_LABEL_TH,
  DISBURSEMENT_METHODS,
  DISBURSEMENT_METHOD_LABEL_TH,
} from '@foundation/shared';
import { api } from '../lib/api';
import { formatDate, formatDateTime, formatSatang } from '../lib/format';
import { ErrorAlert, Loading, Modal } from '../components/ui';
import { useAuth } from '../auth';

const STATUS_TONE: Record<string, string> = {
  draft: '',
  submitted: 'amber',
  under_review: 'amber',
  approved: 'green',
  rejected: 'red',
  in_progress: 'blue',
  completed: 'green',
  cancelled: '',
};

/** สตางค์ ↔ บาท ในช่องกรอก — ผู้ใช้คิดเป็นบาท ระบบเก็บเป็นสตางค์ */
const toSatang = (baht: string): number => Math.round(Number(baht || '0') * 100);
const toBaht = (satang: number | null): string =>
  satang === null ? '' : String(Math.round(satang) / 100);

export default function CaseDetail() {
  const { id = '' } = useParams();
  const { allowed, me } = useAuth();
  const [data, setData] = useState<CaseDetailType | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [disburseOpen, setDisburseOpen] = useState(false);
  const [allocOpen, setAllocOpen] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await api<CaseDetailType>(`/cases/${id}`));
    } catch (e) {
      setError(e);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act<T>(fn: () => Promise<T>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
      setApproveOpen(false);
      setRejectOpen(false);
      setDisburseOpen(false);
      setAllocOpen(false);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (!data) return error ? <ErrorAlert error={error} /> : <Loading />;

  const can = (s: CaseStatus) => data.availableTransitions.includes(s);
  const canApprove = allowed('case:approve_l1') && can('approved');
  const isMine = data.assignedStaffId === me?.id;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>
            {data.caseCode}{' '}
            <span className={`badge ${STATUS_TONE[data.status]}`}>
              {CASE_STATUS_LABEL_TH[data.status]}
            </span>
          </h1>
          <p>
            {data.title} · {data.aidTypeName} · ความเร่งด่วน
            {CASE_PRIORITY_LABEL_TH[data.priority]}
          </p>
        </div>
        <Link className="btn ghost" to="/cases">
          กลับ
        </Link>
      </div>

      <ErrorAlert error={error} />

      <div className="grid grid-2">
        <div className="card">
          <h2>ข้อมูลเคส</h2>
          <dl className="detail-list">
            <dt>ผู้รับความช่วยเหลือ</dt>
            <dd>
              <Link to={`/beneficiaries/${data.beneficiaryId}`}>{data.beneficiaryName}</Link>
              <div className="muted small">
                {data.benCode}
                {data.areaName ? ` · ${data.areaName}` : ''}
              </div>
            </dd>

            <dt>ยอดที่ขอ</dt>
            <dd>{formatSatang(data.requestedAmount)}</dd>

            <dt>ยอดที่อนุมัติ</dt>
            <dd style={{ fontWeight: 600 }}>{formatSatang(data.approvedAmount)}</dd>

            <dt>จ่ายไปแล้ว</dt>
            <dd>
              {formatSatang(data.disbursedAmount)}
              {data.approvedAmount !== null && data.approvedAmount > 0 && (
                <div className="muted small">คงเหลือ {formatSatang(data.remainingAmount)}</div>
              )}
            </dd>

            <dt>เงินบริจาคที่จัดสรรมา</dt>
            <dd>
              {formatSatang(data.allocatedAmount)}
              {data.disbursedAmount > data.allocatedAmount && (
                <div className="muted small">
                  ยังไม่ได้ระบุที่มาของเงิน {formatSatang(data.disbursedAmount - data.allocatedAmount)}
                </div>
              )}
            </dd>

            <dt>ระดับอนุมัติที่ต้องใช้</dt>
            <dd>ระดับ {data.requiredApprovalLevel}</dd>

            <dt>ผู้รับผิดชอบ</dt>
            <dd>
              {data.assignedStaffName ?? '—'}
              {isMine && <span className="badge blue"> งานของฉัน</span>}
            </dd>

            <dt>กำหนดเสร็จ</dt>
            <dd>
              {formatDate(data.dueDate)}
              {data.isOverdue && <span className="badge red"> เลยกำหนด</span>}
            </dd>

            {data.description && (
              <>
                <dt>รายละเอียด</dt>
                <dd className="wrap">{data.description}</dd>
              </>
            )}

            {data.approvedByName && (
              <>
                <dt>อนุมัติโดย</dt>
                <dd>
                  {data.approvedByName}
                  <div className="muted small">{formatDateTime(data.approvedAt)}</div>
                </dd>
              </>
            )}

            {data.rejectReason && (
              <>
                <dt>เหตุผลที่ไม่อนุมัติ</dt>
                <dd className="wrap">
                  {data.rejectReason}
                  <div className="muted small">
                    {data.rejectedByName} · {formatDateTime(data.rejectedAt)}
                  </div>
                </dd>
              </>
            )}
          </dl>

          <div className="btn-row">
            {allowed('case:write') && can('submitted') && (
              <button
                className="btn"
                disabled={busy}
                onClick={() =>
                  void act(() => api(`/cases/${id}/submit`, { method: 'POST', body: {} }))
                }
              >
                ยื่นขออนุมัติ
              </button>
            )}
            {canApprove && (
              <button className="btn" disabled={busy} onClick={() => setApproveOpen(true)}>
                อนุมัติ
              </button>
            )}
            {allowed('case:approve_l1') && data.availableTransitions.includes('rejected') && (
              <button className="btn danger" disabled={busy} onClick={() => setRejectOpen(true)}>
                ไม่อนุมัติ
              </button>
            )}
            {allowed('case:write') && can('in_progress') && (
              <button
                className="btn ghost"
                disabled={busy}
                onClick={() =>
                  void act(() =>
                    api(`/cases/${id}/status`, {
                      method: 'POST',
                      body: { status: 'in_progress' },
                    }),
                  )
                }
              >
                เริ่มดำเนินการ
              </button>
            )}
            {allowed('case:write') && can('completed') && (
              <button
                className="btn"
                disabled={busy}
                onClick={() =>
                  void act(() =>
                    api(`/cases/${id}/status`, { method: 'POST', body: { status: 'completed' } }),
                  )
                }
              >
                ปิดเคส
              </button>
            )}
            {allowed('case:write') && can('cancelled') && (
              <button
                className="btn ghost"
                disabled={busy}
                onClick={() => {
                  const note = window.prompt('เหตุผลที่ยกเลิกเคส');
                  if (!note) return;
                  void act(() =>
                    api(`/cases/${id}/status`, {
                      method: 'POST',
                      body: { status: 'cancelled', note },
                    }),
                  );
                }}
              >
                ยกเลิกเคส
              </button>
            )}
          </div>

          {allowed('case:approve_l1') && !canApprove && data.status === 'submitted' && (
            <p className="hint">
              คุณอนุมัติเคสนี้ไม่ได้ — ต้องใช้ผู้มีอำนาจระดับ {data.requiredApprovalLevel}
              {data.createdByName ? ` หรือคุณเป็นผู้เปิดเคสนี้เอง` : ''}
            </p>
          )}
        </div>

        <div className="card">
          <h2>การจ่ายความช่วยเหลือ</h2>

          {data.disbursements.length === 0 ? (
            <p className="muted">ยังไม่มีการจ่าย</p>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>วันที่</th>
                    <th>ยอด</th>
                    <th>วิธี</th>
                    <th>หลักฐาน</th>
                    <th>ผู้จ่าย</th>
                    <th>ตรวจรับ</th>
                  </tr>
                </thead>
                <tbody>
                  {data.disbursements.map((d) => (
                    <tr key={d.id}>
                      <td className="small muted">{formatDateTime(d.disbursedAt)}</td>
                      <td style={{ fontWeight: 600 }}>
                        {formatSatang(d.amount)}
                        {d.goodsDetail && <div className="muted small wrap">{d.goodsDetail}</div>}
                      </td>
                      <td className="small">{DISBURSEMENT_METHOD_LABEL_TH[d.method]}</td>
                      <td className="small">
                        {d.hasSignature && <span className="badge green">ลายเซ็น</span>}{' '}
                        {d.hasPhoto && <span className="badge">รูป</span>}{' '}
                        {d.gpsLat !== null && <span className="badge">พิกัด</span>}
                      </td>
                      <td className="small">{d.disbursedByName}</td>
                      <td className="small">
                        {d.verifiedAt ? (
                          <span className="badge green">ตรวจรับแล้ว</span>
                        ) : allowed('donation:verify') && d.disbursedById !== me?.id ? (
                          <button
                            className="btn ghost small"
                            disabled={busy}
                            onClick={() =>
                              void act(() =>
                                api(`/cases/${id}/disbursements/${d.id}/verify`, {
                                  method: 'POST',
                                  body: {},
                                }),
                              )
                            }
                          >
                            ตรวจรับ
                          </button>
                        ) : (
                          <span className="badge amber">รอตรวจรับ</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="btn-row">
            {allowed('case:write') && ['approved', 'in_progress'].includes(data.status) && (
              <button className="btn" disabled={busy} onClick={() => setDisburseOpen(true)}>
                + บันทึกการจ่าย
              </button>
            )}
            {allowed('donation:write') && data.disbursedAmount > 0 && (
              <button className="btn ghost" disabled={busy} onClick={() => setAllocOpen(true)}>
                ระบุที่มาของเงิน
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <h2>ประวัติสถานะ</h2>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>เวลา</th>
                <th>เปลี่ยนเป็น</th>
                <th>โดย</th>
                <th className="wrap">บันทึก</th>
              </tr>
            </thead>
            <tbody>
              {data.history.map((h) => (
                <tr key={h.id}>
                  <td className="small muted">{formatDateTime(h.createdAt)}</td>
                  <td>
                    <span className={`badge ${STATUS_TONE[h.toStatus]}`}>
                      {CASE_STATUS_LABEL_TH[h.toStatus]}
                    </span>
                  </td>
                  <td className="small">{h.actorName ?? 'ระบบ'}</td>
                  <td className="wrap small">{h.note ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {approveOpen && (
        <ApproveModal
          detail={data}
          busy={busy}
          onClose={() => setApproveOpen(false)}
          onSubmit={(body) =>
            void act(() => api(`/cases/${id}/approve`, { method: 'POST', body }))
          }
        />
      )}

      {rejectOpen && (
        <RejectModal
          busy={busy}
          onClose={() => setRejectOpen(false)}
          onSubmit={(body) => void act(() => api(`/cases/${id}/reject`, { method: 'POST', body }))}
        />
      )}

      {disburseOpen && (
        <DisburseModal
          detail={data}
          busy={busy}
          onClose={() => setDisburseOpen(false)}
          onSubmit={(body) =>
            void act(() =>
              api(`/cases/${id}/disbursements`, {
                method: 'POST',
                body,
        idempotent: true,
              }),
            )
          }
        />
      )}

      {allocOpen && (
        <AllocateModal
          caseId={id}
          detail={data}
          busy={busy}
          onClose={() => setAllocOpen(false)}
          onDone={() => void act(async () => undefined)}
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------------ modals */

function ApproveModal({
  detail,
  busy,
  onClose,
  onSubmit,
}: {
  detail: CaseDetailType;
  busy: boolean;
  onClose: () => void;
  onSubmit: (body: { approvedAmount: number; note?: string }) => void;
}) {
  const [amount, setAmount] = useState(toBaht(detail.requestedAmount));
  const [note, setNote] = useState('');
  const satang = toSatang(amount);
  const overRequest = detail.requestedAmount !== null && satang > detail.requestedAmount;

  return (
    <Modal title={`อนุมัติเคส ${detail.caseCode}`} onClose={onClose}>
      <div className="field">
        <label htmlFor="amt">ยอดที่อนุมัติ (บาท)</label>
        <input id="amt" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        <p className="hint">
          ยอดที่ขอมา {formatSatang(detail.requestedAmount)} — อนุมัติน้อยกว่าได้ แต่เกินไม่ได้
        </p>
        {overRequest && <p className="alert error">อนุมัติเกินจำนวนที่ยื่นขอไม่ได้</p>}
      </div>
      <div className="field">
        <label htmlFor="note">บันทึกการพิจารณา</label>
        <textarea id="note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <div className="btn-row">
        <button
          className="btn"
          disabled={busy || satang <= 0 || overRequest}
          onClick={() => onSubmit({ approvedAmount: satang, note: note || undefined })}
        >
          ยืนยันอนุมัติ
        </button>
        <button className="btn ghost" onClick={onClose}>
          ยกเลิก
        </button>
      </div>
    </Modal>
  );
}

function RejectModal({
  busy,
  onClose,
  onSubmit,
}: {
  busy: boolean;
  onClose: () => void;
  onSubmit: (body: { reason: string }) => void;
}) {
  const [reason, setReason] = useState('');
  return (
    <Modal title="ไม่อนุมัติเคส" onClose={onClose}>
      <div className="field">
        <label htmlFor="reason">เหตุผล</label>
        <textarea id="reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        <p className="hint">เหตุผลนี้จะถูกบันทึกไว้ในประวัติเคสอย่างถาวร</p>
      </div>
      <div className="btn-row">
        <button className="btn danger" disabled={busy || reason.trim().length < 5} onClick={() => onSubmit({ reason })}>
          ยืนยันไม่อนุมัติ
        </button>
        <button className="btn ghost" onClick={onClose}>
          ยกเลิก
        </button>
      </div>
    </Modal>
  );
}

function DisburseModal({
  detail,
  busy,
  onClose,
  onSubmit,
}: {
  detail: CaseDetailType;
  busy: boolean;
  onClose: () => void;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const [amount, setAmount] = useState(toBaht(detail.remainingAmount));
  const [method, setMethod] = useState<(typeof DISBURSEMENT_METHODS)[number]>('transfer');
  const [goodsDetail, setGoodsDetail] = useState('');
  const [transferRef, setTransferRef] = useState('');
  const [note, setNote] = useState('');

  const satang = toSatang(amount);
  const overRemaining = detail.approvedAmount !== null && satang > detail.remainingAmount;
  const needsGoods = satang === 0 && !goodsDetail.trim();
  // เงินสดต้องมีลายเซ็น ซึ่งเก็บได้จาก LIFF เท่านั้น (วาดบนจอมือถือ)
  const cashOnWeb = method === 'cash';

  return (
    <Modal title="บันทึกการจ่ายความช่วยเหลือ" onClose={onClose}>
      <div className="form-row">
        <div className="field">
          <label htmlFor="damt">จำนวนเงิน (บาท)</label>
          <input id="damt" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
          <p className="hint">ใส่ 0 ได้ถ้าเป็นการมอบสิ่งของล้วน</p>
        </div>
        <div className="field">
          <label htmlFor="dmethod">วิธีจ่าย</label>
          <select id="dmethod" value={method} onChange={(e) => setMethod(e.target.value as typeof method)}>
            {DISBURSEMENT_METHODS.map((m) => (
              <option key={m} value={m}>
                {DISBURSEMENT_METHOD_LABEL_TH[m]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {overRemaining && (
        <p className="alert error">
          เกินวงเงินที่อนุมัติ — คงเหลือ {formatSatang(detail.remainingAmount)}
        </p>
      )}

      {cashOnWeb && (
        <p className="alert warn">
          การจ่ายเงินสดต้องมีลายเซ็นผู้รับ ซึ่งเซ็นได้จากแอป LINE ของเจ้าหน้าที่ภาคสนามเท่านั้น
          กรุณาให้เจ้าหน้าที่บันทึกจากหน้างาน
        </p>
      )}

      <div className="field">
        <label htmlFor="dgoods">รายละเอียดสิ่งของ</label>
        <input id="dgoods" value={goodsDetail} onChange={(e) => setGoodsDetail(e.target.value)} placeholder="เช่น ถุงยังชีพ 2 ชุด" />
      </div>

      {method === 'transfer' && (
        <div className="field">
          <label htmlFor="dref">เลขอ้างอิงการโอน</label>
          <input id="dref" value={transferRef} onChange={(e) => setTransferRef(e.target.value)} />
        </div>
      )}

      <div className="field">
        <label htmlFor="dnote">บันทึกเพิ่มเติม</label>
        <textarea id="dnote" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>

      <div className="btn-row">
        <button
          className="btn"
          disabled={busy || overRemaining || needsGoods || cashOnWeb}
          onClick={() =>
            onSubmit({
              disbursedAt: new Date().toISOString(),
              amount: satang,
              method,
              goodsDetail: goodsDetail || undefined,
              bankAccountId: method === 'transfer' ? 'ba_main' : undefined,
              transferRef: transferRef || undefined,
              note: note || undefined,
            })
          }
        >
          บันทึกการจ่าย
        </button>
        <button className="btn ghost" onClick={onClose}>
          ยกเลิก
        </button>
      </div>
    </Modal>
  );
}

function AllocateModal({
  caseId,
  detail,
  busy,
  onClose,
  onDone,
}: {
  caseId: string;
  detail: CaseDetailType;
  busy: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [donationId, setDonationId] = useState('');
  const [state, setState] = useState<DonationAllocationState | null>(null);
  const [existing, setExisting] = useState<AllocationSummary[]>([]);
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    api<{ data: AllocationSummary[] }>(`/allocations?aidCaseId=${caseId}`)
      .then((r) => setExisting(r.data))
      .catch(() => setExisting([]));
  }, [caseId]);

  async function lookup() {
    setError(null);
    setState(null);
    try {
      setState(await api<DonationAllocationState>(`/allocations/donation/${donationId.trim()}`));
    } catch (e) {
      setError(e);
    }
  }

  async function submit() {
    setWorking(true);
    setError(null);
    try {
      await api('/allocations', {
        method: 'POST',
        body: { donationId: donationId.trim(), aidCaseId: caseId, amount: toSatang(amount) },
        idempotent: true,
      });
      onDone();
    } catch (e) {
      setError(e);
    } finally {
      setWorking(false);
    }
  }

  const unlinked = detail.disbursedAmount - detail.allocatedAmount;

  return (
    <Modal title="ระบุที่มาของเงินที่จ่ายไป" onClose={onClose}>
      <p className="hint">
        ผูกเงินบริจาคก้อนไหนกับเคสนี้ เพื่อตอบผู้บริจาคได้ว่าเงินของเขาไปช่วยใคร
        {unlinked > 0 && ` — ยังไม่ได้ระบุที่มา ${formatSatang(unlinked)}`}
      </p>

      <ErrorAlert error={error} />

      {existing.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>เลขที่บริจาค</th>
                <th>ผู้บริจาค</th>
                <th>ยอดที่จัดสรร</th>
              </tr>
            </thead>
            <tbody>
              {existing.map((a) => (
                <tr key={a.id}>
                  <td className="mono small">{a.donationNo}</td>
                  <td className="small">{a.donorName ?? 'ไม่ระบุนาม'}</td>
                  <td>{formatSatang(a.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="field">
        <label htmlFor="did">รหัสรายการบริจาค</label>
        <div className="form-row">
          <input
            id="did"
            className="mono"
            value={donationId}
            onChange={(e) => setDonationId(e.target.value)}
            placeholder="dn_…"
          />
          <button className="btn ghost" disabled={!donationId.trim()} onClick={() => void lookup()}>
            ตรวจยอด
          </button>
        </div>
        <p className="hint">คัดลอกรหัสได้จากหน้ารายละเอียดเงินบริจาค</p>
      </div>

      {state && (
        <>
          <dl className="detail-list">
            <dt>เลขที่</dt>
            <dd className="mono">{state.donationNo}</dd>
            <dt>ยอดบริจาค</dt>
            <dd>{formatSatang(state.amount)}</dd>
            <dt>จัดสรรไปแล้ว</dt>
            <dd>{formatSatang(state.allocatedAmount)}</dd>
            <dt>คงเหลือจัดสรรได้</dt>
            <dd style={{ fontWeight: 600 }}>{formatSatang(state.remainingAmount)}</dd>
          </dl>

          <div className="field">
            <label htmlFor="aamt">จัดสรรเข้าเคสนี้ (บาท)</label>
            <input
              id="aamt"
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            {toSatang(amount) > state.remainingAmount && (
              <p className="alert error">เกินยอดคงเหลือของเงินก้อนนี้</p>
            )}
          </div>
        </>
      )}

      <div className="btn-row">
        <button
          className="btn"
          disabled={
            busy ||
            working ||
            !state ||
            toSatang(amount) <= 0 ||
            toSatang(amount) > (state?.remainingAmount ?? 0)
          }
          onClick={() => void submit()}
        >
          จัดสรรเข้าเคสนี้
        </button>
        <button className="btn ghost" onClick={onClose}>
          ปิด
        </button>
      </div>
    </Modal>
  );
}
